import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';
import { DateInput } from '../../../../shared/components/DateInput.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface TrainingRow {
  readonly trainingProgramId: string;
  readonly programCode: string;
  readonly programName: string;
  readonly recordKind: string;
  readonly trainingType: string;
  readonly completionStatus: string;
  readonly assigneeId: string | null;
  readonly dueDate: string | null;
  readonly status: string;
}

const RECORD_KINDS = ['', 'PROGRAM', 'ASSIGNMENT', 'ENROLMENT'];
const COMPLETION = ['', 'NOT_STARTED', 'IN_PROGRESS', 'COMPLETED', 'FAILED', 'OVERDUE', 'WAIVED'];
const TRAINING_TYPES = ['ONBOARDING', 'COMPLIANCE', 'TECHNICAL', 'SOFT_SKILL', 'LEADERSHIP', 'SAFETY', 'PRODUCT'];

/**
 * M35 — Training Assignment Dashboard (§33). Register of training records
 * (T470) — programmes, assignments and enrolments — carrying the §11
 * completion status (NOT_STARTED → COMPLETED) and the due date. Administrators
 * assign a training programme to an employee (assignee) which creates an
 * ASSIGNMENT record; the list is filterable by record kind, completion status,
 * assignee and free-text search. Tenant isolation is enforced server-side
 * (LAW 4).
 */
export function TrainingAssignmentDashboard(): ReactNode {
  const { day } = useBusinessDay();
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<TrainingRow[]>([]);
  const [total, setTotal] = useState(0);
  const [recordKind, setRecordKind] = useState('');
  const [completionStatus, setCompletionStatus] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [q, setQ] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [newProgramName, setNewProgramName] = useState('');
  const [newAssigneeId, setNewAssigneeId] = useState('');
  const [newTrainingType, setNewTrainingType] = useState(TRAINING_TYPES[0]);
  const [newDueDate, setNewDueDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [created, setCreated] = useState<TrainingRow | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (recordKind) params.set('recordKind', recordKind);
    if (completionStatus) params.set('completionStatus', completionStatus);
    if (assigneeId) params.set('assigneeId', assigneeId);
    if (q) params.set('q', q);
    const qs = params.toString();
    apiRequest<{ data: TrainingRow[]; total: number }>(`/v1/kms/training${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, recordKind, completionStatus, assigneeId, q]);

  useEffect(() => load(), [load]);

  const act = async (r: TrainingRow, op: string, label: string, perm: string, body?: unknown): Promise<void> => {
    setActionError(null);
    setNotice(null);
    setBusy(true);
    try {
      await apiRequest(`/v1/kms/training/${r.trainingProgramId}/${op}`, { method: 'POST', body, token });
      setNotice(t('m35.common.notice', { code: r.programCode, label }));
      load();
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m35.common.notAuthorisedAction', { perm }));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  const remove = async (r: TrainingRow): Promise<void> => {
    setActionError(null);
    setNotice(null);
    setBusy(true);
    try {
      await apiRequest(`/v1/kms/training/${r.trainingProgramId}`, { method: 'DELETE', token });
      setNotice(t('m35.common.notice', { code: r.programCode, label: t('m35.common.act.deleted') }));
      load();
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m35.training.notAuthorisedManage'));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  const assign = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setCreated(null);
    if (!newProgramName.trim()) {
      setActionError(t('m35.training.programRequired'));
      return;
    }
    if (!newAssigneeId.trim()) {
      setActionError(t('m35.training.assigneeRequired'));
      return;
    }
    setBusy(true);
    try {
      const r = await apiRequest<{ data: TrainingRow }>(`/v1/kms/training/assignments`, {
        method: 'POST',
        body: {
          programName: newProgramName.trim(),
          assigneeId: newAssigneeId.trim(),
          trainingType: newTrainingType,
          dueDate: newDueDate || undefined,
        },
        token,
      });
      setCreated(r.data);
      setNewProgramName('');
      setNewAssigneeId('');
      setNewDueDate('');
      load();
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m35.training.notAuthorisedAssign'));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <main aria-label="training-dashboard">
      <h1>{t('m35.training.title')}</h1>

      <form aria-label="assign-form" onSubmit={(e) => void assign(e)}>
        <label>
          {t('m35.training.programName')}
          <input aria-label="field-program" value={newProgramName} onChange={(e) => setNewProgramName(e.target.value)} />
        </label>
        <label>
          {t('m35.training.assignee')}
          <input aria-label="field-assignee" value={newAssigneeId} onChange={(e) => setNewAssigneeId(e.target.value)} placeholder={t('m35.training.assigneePh')} />
        </label>
        <label>
          {t('m35.training.trainingType')}
          <select aria-label="field-trainingtype" value={newTrainingType} onChange={(e) => setNewTrainingType(e.target.value)}>
            {TRAINING_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label>
          {t('m35.training.dueDate')}
          <DateInput aria-label="field-duedate" type="date" value={newDueDate} onValueChange={(nextValue) => setNewDueDate(nextValue)} />
        </label>
        <Button variant="primary" type="submit" aria-label="submit-assign" disabled={busy}>{busy ? t('m35.training.assigning') : t('m35.training.assignBtn')}</Button>
      </form>
      {actionError && <p role="alert" aria-label="training-action-error">{actionError}</p>}
      {notice && <p aria-label="training-action-notice">{notice}</p>}
      {created && (
        <dl role="status" aria-label="assign-result">
          <dt>{t('m35.training.resultCode')}</dt><dd aria-label="result-code">{created.programCode}</dd>
          <dt>{t('m35.training.resultName')}</dt><dd aria-label="result-name">{created.programName}</dd>
          <dt>{t('m35.training.resultCompletion')}</dt><dd aria-label="result-completion">{created.completionStatus}</dd>
        </dl>
      )}

      <div className="filters">
        <label>
          {t('m35.training.kind')}
          <select aria-label="filter-kind" value={recordKind} onChange={(e) => setRecordKind(e.target.value)}>
            {RECORD_KINDS.map((s) => <option key={s || 'all'} value={s}>{s || t('m35.common.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m35.training.completion')}
          <select aria-label="filter-completion" value={completionStatus} onChange={(e) => setCompletionStatus(e.target.value)}>
            {COMPLETION.map((s) => <option key={s || 'all'} value={s}>{s || t('m35.common.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m35.training.assignee')}
          <input aria-label="filter-assignee" value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)} placeholder={t('m35.training.assigneePh')} />
        </label>
        <label>
          {t('m35.common.search')}
          <input aria-label="filter-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('m35.training.searchPh')} />
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m35.common.loading')}</p> : (
        <>
          <p aria-label="training-count">{t('m35.training.count', { n: total })}</p>
          <DataTable<TrainingRow>
            ariaLabel="training-table"
            testId="training-table"
            rowKey={(r) => r.trainingProgramId}
            rowDataAttrs={(r) => ({ 'data-completion': r.completionStatus, 'data-kind': r.recordKind })}
            rows={rows}
            columns={[
              { key: 'code', header: t('m35.common.code'), render: (r) => r.programCode },
              { key: 'programme', header: t('m35.training.colProgramme'), render: (r) => r.programName },
              { key: 'kind', header: t('m35.training.kind'), render: (r) => r.recordKind },
              { key: 'type', header: t('m35.common.type'), render: (r) => r.trainingType },
              { key: 'assignee', header: t('m35.training.assignee'), render: (r) => r.assigneeId ?? '—', cellAriaLabel: (r) => `training-assignee-${r.programCode}` },
              { key: 'due', header: t('m35.training.colDue'), render: (r) => day(r.dueDate) },
              { key: 'completion', header: t('m35.training.completion'), render: (r) => r.completionStatus },
              { key: 'status', header: t('m35.common.status'), render: (r) => r.status },
              { key: 'actions', header: t('m35.common.actions'), render: (r) => (
                <>
                  {r.completionStatus === 'NOT_STARTED' && (
                    <Button variant="secondary" type="button" aria-label={`start-${r.programCode}`} disabled={busy} onClick={() => void act(r, 'start', t('m35.common.act.started'), 'TRAINING_RECORD')}>{t('m35.common.btnStart')}</Button>
                  )}
                  {r.completionStatus === 'IN_PROGRESS' && (
                    <Button variant="secondary" type="button" aria-label={`complete-${r.programCode}`} disabled={busy} onClick={() => void act(r, 'complete', t('m35.common.act.completed'), 'TRAINING_RECORD', {})}>{t('m35.common.btnComplete')}</Button>
                  )}
                  <Button variant="danger" type="button" aria-label={`delete-${r.programCode}`} disabled={busy} onClick={() => void remove(r)}>{t('m35.common.delete')}</Button>
                </>
              ) },
            ]}
          />
        </>
      )}
    </main>
  );
}
